import { eq } from '@art-suite/art-core-ts';
import {
  type FloorPlanRoom,
  getAssemblyGroups,
  getGroupFloorPlanRoom,
  type ScanRootOptimized,
  withDefaultCeiling,
} from '@kaiizen/kai-scan-template-system';
import { groupNameStylesPresenter, groupShortNamePresenter } from './scanPresenters';

/** One room of the floor plan view: the room group's id, its names, and the room it draws as. */
export interface AssemblyRoomPresentation {
  id: string;
  /** The name the plan shows by default. */
  name: string;
  /** The name in each naming style the floor plan offers. */
  names: ReturnType<typeof groupNameStylesPresenter>;
  room: FloorPlanRoom;
}

/**
 * The rooms the floor plan view lays out: every room group with a floor plan
 * (drawn, or a rectangle synthesized from legacy dimensions), keyed by group
 * id, in the standard group order and named in every style the settings
 * offer. Groups with no measurements have no shape to place and are left out.
 * A room with no ceiling height gets the default one to draw with; saves take
 * it off again (`getScanWithAssembledRooms`).
 *
 * Returns `previousRooms` itself when the new rooms equal them deeply, so the
 * rooms keep their identity until the floor plan data changes and a scan
 * update elsewhere (photos, notes, other groups) reaches the view as no change.
 */
export const getAssemblyRooms = (scanRoot: ScanRootOptimized | undefined, previousRooms?: AssemblyRoomPresentation[]): AssemblyRoomPresentation[] => {
  const rooms = scanRoot
    ? getAssemblyGroups(scanRoot).flatMap(group => {
      const room = getGroupFloorPlanRoom(group);
      return room ? [{ id: group.id, name: groupShortNamePresenter(group), names: groupNameStylesPresenter(group), room: withDefaultCeiling(room) }] : [];
    })
    : [];
  return previousRooms && eq(previousRooms, rooms) ? previousRooms : rooms;
};
